<template>
  <div class="line-item">
    <div class="line-item__preview">
      <v-image
        :src="
          lineartCase
            ? 'https://static-production.gocase.com.br/products/65746/product/mockup.jpg?1680717843'
            : currentVariantImage
        "
        :responsive="true"
        alt="Mockup"
      />
    </div>
    <div class="line-item__body">
      <div class="line-item__title">
        <nuxt-link
          v-if="!lineItem.gift"
          :to="`/${lineItem.slug}/p`"
          @click.native="
            $store.commit('CURRENT_CLICKED_PRODUCT_ID', lineItem.product_id)
          "
        >
          {{ currentVariantName }}</nuxt-link
        >
        <div v-else>{{ currentVariantName }}</div>
      </div>

      <p v-if="isKit" class="line-item__option-kit">
        📦 Kit:
        <b>{{ lineItem.kit_product_name || lineItem.kit_info.kit_name }}</b>
      </p>

      <line-item-customizations
        v-if="currentVariantImage && !lineartCase"
        :gift-prop="lineItem.gift"
        :customizations-prop="customizations"
        :preview-image="currentVariantImage"
        :show-customizations-prop="true"
      />

      <div v-if="lineItem.case_device || surpriseCase">
        <span class="line-item__option-title">{{ $t('device') }}:</span>
        <b>{{
          surpriseCase && !lineItem.case_device
            ? $t('not_selected')
            : caseDeviceName
        }}</b>
      </div>

      <v-link
        v-if="lineItem.ebook_link"
        :href="lineItem.ebook_link"
        target="_blank"
        class="line-item-ebook-link"
        :label="$t('view_ebook')"
      />

      <line-item-pricing
        :kit-prop="isKit"
        :gift-prop="lineItem.gift"
        :old-price-prop="
          lineItem.price === lineItem.old_price ? null : lineItem.old_price
        "
        :price-prop="lineItem.price"
        :quantity-prop="lineItem.quantity"
        :show-quantity-prop="showQuantity"
      />

      <span v-if="showActions && !lineItem.in_stock" class="out-of-stock">
        {{ $t('sold_off') }}.
        <a
          href="#"
          rel="nofollow"
          class="delete out-of-stock__remove remove-from-cart-gtm"
          @click.prevent="updateQuantity(0)"
        >
          {{ $t('cart.remove_item') }}?
        </a>
      </span>

      <span
        v-if="showActions && lineItem.discontinued_case_device"
        class="out-of-stock"
      >
        {{ $t('discontinued_case_device') }}.
        <a
          href="#"
          rel="nofollow"
          class="delete out-of-stock__remove remove-from-cart-gtm"
          @click.prevent="updateQuantity(0)"
        >
          {{ $t('cart.remove_item') }}?
        </a>
      </span>

      <div v-if="lineartCase" class="line-item__ondemand">
        {{ $t('lineart_notice') }}
      </div>
      <div
        v-else-if="lineItem.additional_days && !lineItem.gift"
        class="line-item__ondemand"
      >
        {{ $t('line_item_on_demand', { days: lineItem.additional_days }) }}
      </div>

      <line-item-actions
        :line-item-quantity-prop="lineItem.quantity"
        :current-variant-id-prop="currentVariantId"
        :line-item-id-prop="lineItem.id"
        :gift-prop="lineItem.gift"
        :kit-prop="isKit"
        :choosable-gift-prop="lineItem.choosable_gift && canShowChangeBtn"
        :gift-options-prop="lineItem.gift_options_enabled && canShowChangeBtn"
        :promotion-event-stock-prop="lineItem.promotion_event_stock"
        :show-actions-prop="showActions"
        :show-select-quantity-prop="showSelectQuantity"
        :show-remove-prop="showRemove || (lineItem.gift && isKit)"
        @updateQuantity="updateQuantity"
        @newVariant="newCurrentVariant"
      />
      <transition v-if="canShowChangeBtn && !isKit && canEditLn" name="fade">
        <line-item-editor
          v-show="canShowLineItemEditor"
          :line-item="lineItem"
          :editable-order-product="editableOrder"
          :order-number="orderNumber"
          @updatedLineItem="updatedLineItem"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import LineItemEditor from './line-item-editor/LineItemEditor.vue'
import VImage from '~/components/VImage.vue'
import VLink from '~/components/v-link/VLink.vue'
import LineItemPricing from '~/components/line-item/line-item-pricing/LineItemPricing.vue'
import LineItemCustomizations from '~/components/line-item/line-item-customizations/LineItemCustomizations.vue'
import LineItemActions from '~/components/line-item/line-item-actions/LineItemActions.vue'
import appendUrlParam from '~/utils/appendUrlParam'
import pushDataLayer from '~/mixins/gtm/push-data-layer'
import isPresent from '~/utils/isPresent'
import Ga4Parser from '~/utils/Ga4Parser'

export default {
  components: {
    VImage,
    VLink,
    LineItemPricing,
    LineItemCustomizations,
    LineItemActions,
    LineItemEditor,
  },
  mixins: [pushDataLayer],
  props: {
    lineItem: {
      type: Object,
      default: () => {},
    },
    showPromotion: {
      type: Boolean,
      default: true,
    },
    orderNumber: {
      type: [Number, String],
      default: null,
    },
    showQuantity: {
      type: Boolean,
      default: true,
    },
    showSelectQuantity: {
      type: Boolean,
      default: true,
    },
    showRemove: {
      type: Boolean,
      default: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
    canShowChangeBtn: {
      type: Boolean,
      default: true,
    },
    editableOrder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentVariantId: 0,
      currentVariantName: '',
      currentVariantImage: '',
    }
  },
  computed: {
    canShowLineItemEditor() {
      return true
    },
    lineartCase() {
      return this.lineItem.sku.match('lineart')
    },
    caseDeviceName() {
      if (this.$isGlobalStore) {
        if (this.lineItem.case_device.match('650ml')) {
          const result = this.lineItem.case_device.replace('650ml', '22oz')
          return result
        }
        if (this.lineItem.case_device.match('950ml')) {
          const result = this.lineItem.case_device.replace('950ml', '32oz')
          return result
        }
      }

      return this.lineItem.case_device
    },
    isKit() {
      return this.lineItem.belongs_to_kit || isPresent(this.lineItem.kit_info)
    },
    canEditLn() {
      if (this.lineItem.gift) return this.canEditGift

      // eslint-disable-next-line camelcase
      if (!this.lineItem?.case_device_id && !isPresent(this.customizations))
        return false

      let canShow = true

      for (const customization in this.customizations) {
        if (
          ['Customizr', 'Portinari', 'Pikaso'].includes(customization) ||
          this.lineItem.image.match('portinari')
        ) {
          canShow = false
        }
      }
      return canShow
    },
    customizations() {
      const { customizations } = this.lineItem
      if (!customizations) return null
      return customizations
    },
    canEditGift() {
      return (
        this.lineItem.gift &&
        (isPresent(this.customizations) || this.lineItem.editable)
      )
    },
    surpriseCase() {
      return this.lineItem.sku.match('case-surpresa')
    },
  },
  watch: {
    lineItem(newLn) {
      this.updateCurrentVariant(newLn.variant_id, newLn.name, newLn.image)
    },
  },
  created() {
    this.updateCurrentVariant(
      this.lineItem.variant_id,
      this.lineItem.name,
      this.lineItem.image
    )
  },

  methods: {
    async updatedLineItem() {
      if (!this.lineItem.gift) {
        await this.updateQuantity(0)
      }
      this.$emit('updatedLineItem')
    },
    newCurrentVariant(newVariant) {
      this.updateCurrentVariant(
        newVariant.variant_id,
        newVariant.name,
        newVariant.image
      )
      this.$emit('updatedLineItem')
    },
    updateCurrentVariant(id, name, image) {
      this.currentVariantId = id
      this.currentVariantName = name
      this.currentVariantImage = image
    },
    async updateQuantity(newQuantity) {
      this.$displayOverlaidLoading()

      try {
        const params = {
          ...this.$store.getters.storeInfo,
        }

        let cartAndGamification = {}

        if (this.lineItem.kit_product_id) {
          cartAndGamification = await this.$axios.$post(
            appendUrlParam('carts/remove_kit', params),
            {
              kit: {
                kit_product_id: this.lineItem.kit_product_id,
                token: this.lineItem.kit_token,
              },
            }
          )
        } else if (isPresent(this.lineItem.kit_info)) {
          cartAndGamification = await this.$axios.$post(
            appendUrlParam('kits/remove', params),
            {
              kit: {
                kit_id: this.lineItem.kit_info.kit_id,
                token: this.lineItem.kit_info.token,
              },
            }
          )
        } else {
          cartAndGamification = await this.$axios.$post(
            appendUrlParam('carts/update_contents', params),
            {
              order: {
                line_items_attributes: [
                  {
                    id: this.lineItem.id,
                    quantity: newQuantity,
                  },
                ],
              },
            }
          )
        }

        const { cart } = cartAndGamification

        if (newQuantity === 0) {
          // GA4
          const ga4Items = new Ga4Parser(this.$store.state).removeFromCart(
            this.lineItem
          )

          const ecommerce = new Ga4Parser(this.$store.state).removeFromCartV2(
            this.lineItem,
            cart
          )

          /* 
            Prevent Side Effect since ga4EventValue is an array, 
            gtm automaticly append a new item instead of create a new array  
          */
          this.pushDataLayer({
            ga4EventValue: undefined,
          })

          this.pushDataLayer({
            event: 'genericEvent',
            eventCategory: 'interactWithPage',
            eventAction: 'clickOnButton',
            eventLabel: 'removeFromCart',
            eventValue: this.lineItem,
            ga4EventValue: ga4Items,
            ecommerce,
          })
        }

        this.$emit('updateCart', cart)
        this.newCartQuantity(cart)
      } catch (err) {
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      } finally {
        this.$displayOverlaidLoading('close')
      }
    },
    async updateCaseDevice(newCaseDeviceId) {
      this.$displayOverlaidLoading()
      try {
        const params = {
          ...this.$store.getters.storeInfo,
        }

        const { cart } = await this.$axios.$post(
          appendUrlParam('carts/update_contents', params),
          {
            order: {
              line_items_attributes: [
                {
                  id: this.lineItem.id,
                  case_device_id: newCaseDeviceId,
                },
              ],
            },
          }
        )

        this.$emit('updateCart', cart)
        this.newCartQuantity(cart)
      } catch (err) {
        this.$displayTopMessage(this.$t('action_error'), {
          type: 'error',
        })
      } finally {
        this.$displayOverlaidLoading('close')
      }
    },
    newCartQuantity(cart) {
      // eslint-disable-next-line camelcase
      const newQnt =
        cart.line_items.length > 1
          ? cart.line_items.map((li) => li.quantity).reduce((a, b) => a + b, 0)
          : cart.line_items[0]?.quantity || 0

      this.$store.commit('SET_CART_QTY', newQnt)
      this.$store.dispatch('cart/handleQuantityUpdated', true)
    },
  },
}
</script>

<style lang="scss" scoped>
$color: $v4_color_primary;
$gutter: $v4_gutter;
$color_line: $v4_color_line_lighter;

.line-item {
  position: relative;
  display: flex;
  font-size: 13px;
  padding: 4 * $v4_gutter;
  border-top: 1px solid $color_line;

  .line-item__description--available_stock {
    margin-top: 1px;
    display: block;
  }

  .line-item__description--available_stock,
  .line-item__description--on-demand {
    color: $color;
    line-height: 20px;
  }

  .line-item__preview {
    width: 100px;
    position: absolute;

    img {
      width: 100%;
    }
  }

  .line-item__body {
    flex: 1;
    position: relative;
    padding: 0 36px 0 112px;
    width: 100%;
    min-height: 90px;

    @media (min-width: $v4_desktop) {
      padding-right: 104px;
    }
  }

  .line-item__title {
    margin-bottom: $gutter;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &,
    a {
      color: $v4_color_text;
      font-weight: 400;
      text-decoration: none;
    }
  }

  .line-item__qty-display {
    color: lighten($v4_color_text, 30%);
  }

  .line-item__option-title {
    display: inline-block;
    margin-top: 2px;
  }

  .line-item__option-kit {
    margin: 0;
  }

  .line-item__ondemand {
    display: inline-block;
    margin-top: 2px;
    padding: 2px 3px;
    background-color: $v4_color_yellow;
  }

  .line-item__price--sale,
  .line-item__price--free {
    color: $v4_color_primary;
  }

  .line-item__price--free {
    font-weight: bold;
  }

  .line-item-ebook-link {
    color: $v4_color_primary;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  @media (max-width: $v4_desktop) {
    margin-left: -15px;
    margin-right: -15px;
    flex-wrap: wrap;

    &:last-child {
      border-bottom: 1px solid $color_line;
      margin-bottom: 15px;
    }
  }
}
</style>
